import { PDFDocument, rgb } from "pdf-lib";

// Signature images can arrive as PNG (canvas draw), JPG, GIF, or anything else
// the upload modal's accept="image/*" lets through — but pdf-lib's embedPng()
// only accepts real PNG bytes and throws "The input is not a PNG file!" for
// anything else. Rather than branching on MIME type (and still failing on
// GIF/WEBP/etc, which pdf-lib doesn't embed natively either), normalize
// whatever we're given to PNG via an offscreen canvas, which handles any
// format a browser can decode.
const normalizeSignatureToPngDataUrl = async (signatureImageData) => {
  if (typeof signatureImageData === "string" && signatureImageData.startsWith("data:image/png")) {
    return signatureImageData;
  }

  let srcUrl;
  if (typeof signatureImageData === "string" && signatureImageData.startsWith("data:image")) {
    srcUrl = signatureImageData;
  } else if (signatureImageData instanceof Blob) {
    srcUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Could not read signature file"));
      reader.readAsDataURL(signatureImageData);
    });
  } else {
    // Raw bytes (ArrayBuffer/Uint8Array/Buffer) with no type info — assume
    // it's already PNG, matching the previous behavior for this input shape.
    return signatureImageData;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Could not read signature image — the file may be corrupted or an unsupported format"));
    img.src = srcUrl;
  });
};

const dataUrlToBytes = (dataUrl) => {
  const base64 = dataUrl.split(",")[1];
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
  return bytes;
};

export const pdfService = {
  // Convert contract draft to PDF
  async convertDraftToPdf(graphService, driveId, draftFilePath, contractTitle) {
    try {
      console.log('[pdfService] Looking for file at path:', draftFilePath);
      console.log('[pdfService] Using drive ID:', driveId);

      // Try direct path first
      let fileMetadata = null;
      try {
        fileMetadata = await graphService.getFileMetadata(driveId, draftFilePath);
      } catch (pathError) {
        console.warn('[pdfService] Direct path failed, trying search method...');

        // If direct path fails, search for the file by name in the folder
        const folderPath = draftFilePath.substring(0, draftFilePath.lastIndexOf('/'));
        const fileName = draftFilePath.substring(draftFilePath.lastIndexOf('/') + 1);

        console.log('[pdfService] Searching in folder:', folderPath);
        console.log('[pdfService] Looking for file:', fileName);

        try {
          const folderContents = await graphService.getFolderContents(driveId, folderPath);
          fileMetadata = folderContents?.value?.find(item =>
            item.name && item.name.toLowerCase() === fileName.toLowerCase() && !item.folder
          );

          if (!fileMetadata) {
            console.error('[pdfService] Available files in folder:', folderContents?.value?.map(i => i.name));
            throw new Error(`File not found: ${fileName}`);
          }
        } catch (searchError) {
          console.error('[pdfService] Search failed:', searchError.message);
          throw new Error(`Cannot find ${fileName} in ${folderPath}: ${searchError.message}`);
        }
      }

      if (!fileMetadata) {
        throw new Error(`Contract draft not found at: ${draftFilePath}`);
      }

      console.log('[pdfService] ✅ Found file:', fileMetadata.name, 'ID:', fileMetadata.id);

      // Download as PDF using the file ID
      const draftFile = await graphService.downloadFileAsPdf(driveId, fileMetadata.id);

      console.log('[pdfService] ✅ Converted draft to PDF:', contractTitle);
      return draftFile; // ArrayBuffer
    } catch (error) {
      console.error('[pdfService] ❌ Error converting to PDF:', error);
      console.error('[pdfService] Path attempted:', draftFilePath);
      throw error;
    }
  },

  // Embed signature image onto PDF at specific location
  async embedSignatureOnPdf(pdfArrayBuffer, signatureImageData, pageNum = 1, x = 50, y = 130, width = 300, height = 120) {
    try {
      const pdfDoc = await PDFDocument.load(pdfArrayBuffer);
      const pages = pdfDoc.getPages();
      const page = pages[pageNum - 1];

      if (!page) {
        throw new Error(`Page ${pageNum} not found in PDF`);
      }

      // Load signature image — normalized to PNG first so PNG, JPG, GIF, etc.
      // (whatever the upload modal accepted) all embed correctly.
      const pngDataUrl = await normalizeSignatureToPngDataUrl(signatureImageData);
      const signatureImage = typeof pngDataUrl === "string"
        ? await pdfDoc.embedPng(dataUrlToBytes(pngDataUrl))
        : await pdfDoc.embedPng(pngDataUrl);
      console.log('[pdfService] ✅ Embedded signature image');

      // Draw signature on page at specified position
      const { height: pageHeight } = page.getSize();
      page.drawImage(signatureImage, {
        x: x || 50,
        y: y || 130,
        width: width || 300,
        height: height || 120,
      });

      const pdfBytes = await pdfDoc.save();
      console.log('[pdfService] ✅ Embedded signature on page', pageNum);
      return pdfBytes;
    } catch (error) {
      console.error('[pdfService] Error embedding signature:', error);
      throw error;
    }
  },

  // Add text boxes to PDF (with coordinate conversion from screen to PDF)
  async addTextBoxesToPdf(pdfArrayBuffer, textBoxes, pageNum = 1, canvasWidth = 800, canvasHeight = 1000) {
    try {
      const pdfDoc = await PDFDocument.load(pdfArrayBuffer);
      const pages = pdfDoc.getPages();
      const page = pages[pageNum - 1];

      if (!page) {
        throw new Error(`Page ${pageNum} not found in PDF`);
      }

      const pdfWidth = 612;
      const pdfHeight = 792;

      textBoxes.forEach((textBox, idx) => {
        if (!textBox.text || textBox.text.trim() === "") {
          console.log(`[pdfService] Skipping empty text box ${idx}`);
          return;
        }

        // Use pre-calculated PDF coordinates (already converted with actual canvas dimensions)
        const pdfX = textBox.pdfX !== undefined ? textBox.pdfX : Math.round((textBox.x / canvasWidth) * pdfWidth);
        const pdfY = textBox.pdfY !== undefined ? textBox.pdfY : Math.round(pdfHeight - ((textBox.y + textBox.height / 2) / canvasHeight) * pdfHeight);

        console.log(`[pdfService] ✍️ Adding text box ${idx + 1} at PDF position:`, { x: pdfX, y: pdfY, text: textBox.text });

        page.drawText(textBox.text, {
          x: pdfX + 20,
          y: pdfY,
          size: 9,
          color: rgb(0, 0, 0),
        });
      });

      const pdfBytes = await pdfDoc.save();
      console.log('[pdfService] ✅ Added', textBoxes.length, 'text boxes to PDF');
      return pdfBytes;
    } catch (error) {
      console.error('[pdfService] Error adding text boxes:', error);
      return pdfArrayBuffer; // Return original if error
    }
  },

  // Add approval text to PDF at specified positions
  async addApprovalTextToPdf(pdfArrayBuffer, approverName, approvalDate, documentTitle, pageNum = 1, nameX = 50, nameY = 100, dateX = 50, dateY = 80, titleX = 50, titleY = 120) {
    try {
      const pdfDoc = await PDFDocument.load(pdfArrayBuffer);
      const pages = pdfDoc.getPages();
      const page = pages[pageNum - 1];

      if (!page) {
        throw new Error(`Page ${pageNum} not found in PDF`);
      }

      // Add approver name at specified position (without "Approved by" prefix)
      if (approverName && nameX !== null && nameY !== null) {
        console.log('[pdfService] ✍️ Adding name at:', { x: nameX, y: nameY });
        page.drawText(approverName, {
          x: nameX,
          y: nameY,
          size: 10,
          color: rgb(0, 0, 0),
        });
      }

      // Add date at specified position with readable format
      if (approvalDate && dateX !== null && dateY !== null) {
        let dateStr;
        if (typeof approvalDate === 'string') {
          // Parse date string and format it
          const date = new Date(approvalDate);
          dateStr = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
        } else {
          dateStr = approvalDate;
        }
        console.log('[pdfService] ✍️ Adding date at:', { x: dateX, y: dateY, text: dateStr });
        page.drawText(dateStr, {
          x: dateX,
          y: dateY,
          size: 9,
          color: rgb(0.3, 0.3, 0.3),
        });
      }

      // Add title at specified position
      if (documentTitle && titleX !== null && titleY !== null) {
        console.log('[pdfService] ✍️ Adding title at:', { x: titleX, y: titleY });
        page.drawText(documentTitle, {
          x: titleX,
          y: titleY,
          size: 9,
          color: rgb(0, 0, 0),
        });
      }

      const pdfBytes = await pdfDoc.save();
      console.log('[pdfService] ✅ Added approval text on page', pageNum);
      return pdfBytes;
    } catch (error) {
      console.error('[pdfService] Error adding approval text:', error);
      throw error;
    }
  },

  // Save PDF to SharePoint
  async savePdfToSharePoint(graphService, driveId, folderPath, pdfBytes, fileName) {
    try {
      const response = await graphService.uploadFile(
        driveId,
        folderPath,
        `${fileName}.pdf`,
        pdfBytes,
        "application/pdf"
      );

      console.log('[pdfService] ✅ PDF saved to SharePoint:', response.id);
      return response;
    } catch (error) {
      console.error('[pdfService] Error saving PDF:', error);
      throw error;
    }
  },

  // Auto-append admin signature to all pages (right footer)
  async appendAdminSignatureToAllPages(pdfArrayBuffer, adminSignatureBase64) {
    try {
      if (!adminSignatureBase64) {
        console.warn('[pdfService] No admin signature provided, skipping auto-append');
        return pdfArrayBuffer;
      }

      const pdfDoc = await PDFDocument.load(pdfArrayBuffer);
      const pages = pdfDoc.getPages();

      // Convert signature to image — normalized to PNG first so PNG, JPG,
      // GIF, etc. (whatever the admin uploaded) all embed correctly.
      const pngDataUrl = await normalizeSignatureToPngDataUrl(adminSignatureBase64);
      const signatureImage = typeof pngDataUrl === "string"
        ? await pdfDoc.embedPng(dataUrlToBytes(pngDataUrl))
        : await pdfDoc.embedPng(pngDataUrl);

      // Append to all pages (right footer area)
      pages.forEach((page, pageIdx) => {
        const { width, height } = page.getSize();
        const sigWidth = 80;
        const sigHeight = 40;
        const marginRight = 30;
        const marginBottom = 30;

        page.drawImage(signatureImage, {
          x: width - sigWidth - marginRight,
          y: marginBottom,
          width: sigWidth,
          height: sigHeight,
        });

        console.log(`[pdfService] ✅ Admin signature appended to page ${pageIdx + 1}`);
      });

      const pdfBytes = await pdfDoc.save();
      console.log('[pdfService] ✅ Admin signature appended to all pages');
      return pdfBytes;
    } catch (error) {
      console.error('[pdfService] Error appending admin signature:', error);
      // Don't throw - allow execution to continue without auto-signature
      return pdfArrayBuffer;
    }
  },

  // Get executed PDF name - uses "Executed-" prefix so it's recognized in contract details
  getExecutedPdfName(originalFileName) {
    try {
      let baseName = originalFileName.replace(/\.(docx|doc|pdf)$/i, "");
      const cleanName = baseName
        .replace(/[_-]/g, " ")
        .trim()
        .substring(0, 50);
      return `Executed-${cleanName}`;
    } catch (error) {
      console.error('[pdfService] Error formatting PDF name:', error);
      return `Executed-Contract-${Date.now()}`;
    }
  },

  // Create PDF metadata record
  async createPdfMetadata(graphService, {
    fileId,
    fileName,
    contractId,
    contractRef,
    executionId,
    approversCount,
    createdAt,
  }) {
    try {
      const metadata = await graphService.createListItem("FileID", {
        FileID: fileId,
        FilePath: fileName,
        DocumentType: "executed_contract",
        ContractID: contractId,
        ContractRefNo: contractRef,
        ExecutionID: executionId,
        ApproversCount: approversCount,
        CreatedDate: createdAt,
      });

      console.log('[pdfService] ✅ PDF metadata created:', metadata.id);
      return metadata;
    } catch (error) {
      console.error('[pdfService] Error creating PDF metadata:', error);
      throw error;
    }
  },

  // Generate approval summary report
  generateApprovalSummary(execution, approvers) {
    try {
      const approvalDetails = approvers
        .map(
          (a) =>
            `${a.ApproverName} (${a.ApproverEmail}) - ${a.Status} on ${
              a.ApprovedAt ? new Date(a.ApprovedAt).toLocaleDateString() : "Pending"
            }`
        )
        .join("\n");

      return `
CONTRACT APPROVAL SUMMARY
========================

Contract: ${execution.ContractTitle}
Reference: ${execution.ContractRefNo}
Initiated: ${new Date(execution.InitiatedAt).toLocaleString()}
Completed: ${execution.CompletedAt ? new Date(execution.CompletedAt).toLocaleString() : "N/A"}

APPROVALS:
${approvalDetails}

Total Approvers: ${execution.TotalApprovers}
Approvals Completed: ${execution.ApproversCompleted}

Status: ${execution.Status}
      `;
    } catch (error) {
      console.error('[pdfService] Error generating summary:', error);
      return "";
    }
  },
};
